import React, { useState, useEffect, useRef } from 'react';
import { Target, Trash2, ClipboardList } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults, AddImageSection,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DEFAULT_HOTSPOT_COLOR = '#0ea5e9'; // default marker color (sky blue) for visibility on most images
const DEFAULT_SETTINGS = { imageUrl: null, hotspots: [], imageNaturalWidth: null, imageNaturalHeight: null, hotspotMarkerColor: DEFAULT_HOTSPOT_COLOR, pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, description: false, descText: '' };

const DUMMY_DATA = {
    question: 'Where on the poster is the main call-to-action?',
    settings: {
        imageUrl: 'https://placehold.co/700x450/fef9c3/713f12?text=Sample+Poster+%E2%80%93+Find+the+CTA',
        imageNaturalWidth: 700,
        imageNaturalHeight: 450,
        hotspots: [
            { id: 'hs1', x: 20, y: 30, radius: 4, label: 'Top-left banner' },
            { id: 'hs2', x: 50, y: 60, radius: 4, label: 'Center button' },
            { id: 'hs3', x: 80, y: 85, radius: 4, label: 'Bottom-right footer' },
        ],
        hotspotMarkerColor: DEFAULT_HOTSPOT_COLOR,
        pollResults: 'hidden', isLocked: false, allowUpdateResponse: true,
        description: true, descText: 'Study the image and click on the hotspot that you think is the primary call-to-action area.',
    },
};

const hexToRgba = (hex, alpha) => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return `rgba(14,165,233,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const Hotspot = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const imgRef = useRef(null);
    const confirm = useConfirm();

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const buildContent = () => ({ question, settings });
    const validate = () => !question.trim() ? 'Please enter a question.' : (!settings.imageUrl ? 'Please upload an image.' : (settings.hotspots.length < 1 ? 'Add at least one hotspot.' : null));
    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const handleImageLoad = () => {
        if (!imgRef.current || settings.imageNaturalWidth != null) return;
        const nw = imgRef.current.naturalWidth;
        const nh = imgRef.current.naturalHeight;
        if (nw && nh) setSettings(s => ({ ...s, imageNaturalWidth: nw, imageNaturalHeight: nh }));
    };

    const handleImageClick = (e) => {
        if (!imgRef.current) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = parseFloat(((e.clientX - rect.left) / rect.width * 100).toFixed(1));
        const y = parseFloat(((e.clientY - rect.top) / rect.height * 100).toFixed(1));
        const nextHotspots = [...settings.hotspots, { id: `hs${Date.now()}`, x, y, radius: 3, label: `Hotspot ${settings.hotspots.length + 1}` }];
        if (!settings.imageNaturalWidth && imgRef.current.naturalWidth && imgRef.current.naturalHeight) {
            setSettings(s => ({
                ...s,
                hotspots: nextHotspots,
                imageNaturalWidth: imgRef.current.naturalWidth,
                imageNaturalHeight: imgRef.current.naturalHeight,
            }));
        } else {
            updateSetting('hotspots', nextHotspots);
        }
    };

    const removeHotspot = async (id) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Hotspot',
            message: 'Delete this hotspot?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This poll already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them clicked this hotspot, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        updateSetting('hotspots', settings.hotspots.filter(h => h.id !== id));
    };
    const updateHotspot = (id, field, val) => updateSetting('hotspots', settings.hotspots.map(h => h.id === id ? { ...h, [field]: val } : h));

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ ...activity, content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Type your question here..." autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ marginTop: 'var(--space-4)' }}>
                {settings.imageUrl ? (
                    <>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Target size={14} /> Click on the image to place hotspots
                        </p>
                        <div style={{ maxHeight: 300, overflow: 'auto', width: '100%' }}>
                            <div
                                style={{
                                    position: 'relative',
                                    cursor: 'crosshair',
                                    border: '2px dashed var(--border)',
                                    borderRadius: 'var(--radius-lg)',
                                    overflow: 'hidden',
                                    width: '100%',
                                    ...(settings.imageNaturalWidth && settings.imageNaturalHeight
                                        ? { aspectRatio: `${settings.imageNaturalWidth} / ${settings.imageNaturalHeight}` }
                                        : { maxHeight: 300 }),
                                }}
                                onClick={handleImageClick}
                            >
                                <img
                                    ref={imgRef}
                                    src={settings.imageUrl}
                                    alt="Hotspot map"
                                    onLoad={handleImageLoad}
                                    style={{
                                        width: '100%',
                                        height: settings.imageNaturalWidth && settings.imageNaturalHeight ? '100%' : undefined,
                                        display: 'block',
                                        objectFit: 'contain',
                                        background: 'var(--bg-tertiary)',
                                        ...(!(settings.imageNaturalWidth && settings.imageNaturalHeight) ? { maxHeight: 300 } : {}),
                                    }}
                                    draggable={false}
                                />
                                {settings.hotspots.map((hs, i) => {
                                    const markerColor = settings.hotspotMarkerColor || DEFAULT_HOTSPOT_COLOR;
                                    return (
                                        <div key={hs.id} style={{ position: 'absolute', left: `${hs.x}%`, top: `${hs.y}%`, transform: 'translate(-50%,-50%)', width: `${hs.radius * 2}%`, aspectRatio: '1', borderRadius: '50%', background: hexToRgba(markerColor, 0.5), border: `2px solid ${hexToRgba(markerColor, 0.9)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#1a1a1a', pointerEvents: 'none' }}>{i + 1}</div>
                                    );
                                })}
                            </div>
                        </div>
                        <button type="button" onClick={() => updateSetting('imageUrl', null)} style={{ marginTop: 'var(--space-2)', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Change image</button>
                    </>
                ) : (
                    <AddImageSection onImageChange={url => updateSetting('imageUrl', url)} />
                )}
            </div>

            {settings.hotspots.length > 0 && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>Hotspot Labels</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                        {settings.hotspots.map((hs, i) => (
                            <div key={hs.id} className={styles.optionRow}>
                                <span className={styles.optionLetter}>{i + 1}</span>
                                <input className={styles.optionInput} value={hs.label} onChange={(e) => updateHotspot(hs.id, 'label', e.target.value)} placeholder="Hotspot label" />
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{hs.x}%, {hs.y}%</span>
                                <button className={styles.optionRemoveBtn} onClick={() => removeHotspot(hs.id)} aria-label="Remove hotspot"><Trash2 size={13} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Hotspot marker color" description="Color of the hotspot spots on the image (for visibility)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <input
                        type="color"
                        value={settings.hotspotMarkerColor || DEFAULT_HOTSPOT_COLOR}
                        onChange={(e) => updateSetting('hotspotMarkerColor', e.target.value)}
                        style={{ width: 36, height: 28, padding: 2, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', background: 'var(--bg-primary)' }}
                        aria-label="Hotspot marker color"
                    />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{settings.hotspotMarkerColor || DEFAULT_HOTSPOT_COLOR}</span>
                </div>
            </SettingsRow>
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                <Toggle checked={settings.allowUpdateResponse} onChange={(e) => updateSetting('allowUpdateResponse', e.target.checked)} />
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<Target size={20} />} color="var(--cat-rose)"
            title="Hotspot Poll" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Hotspot;
